import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { generateSitemaps } from '../../utils/sitemapService';
import { Globe, RefreshCw, Check, Download, AlertTriangle, FileCode, ExternalLink } from 'lucide-react';

const SitemapManager = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastGenerated, setLastGenerated] = useState(null);
    const [config, setConfig] = useState({
        autoUpdate: true,
        notifyOnFail: true
    });

    useEffect(() => {
        const fetchStatus = async () => {
            const docSnap = await getDoc(doc(db, 'settings', 'sitemaps'));
            if (docSnap.exists()) {
                const data = docSnap.data();
                setStatus(data);
                if (data.lastGenerated) {
                    setLastGenerated(data.lastGenerated.toDate().toLocaleString());
                }
            }
        };
        fetchStatus();
    }, []);

    const handleGenerate = async () => {
        setLoading(true);
        const result = await generateSitemaps();
        if (result.success) {
            const docSnap = await getDoc(doc(db, 'settings', 'sitemaps'));
            const data = docSnap.data();
            setStatus(data);
            setLastGenerated(new Date().toLocaleString());
            alert("Sitemaps regenerated successfully!");
        } else {
            alert("Error: " + result.error);
        }
        setLoading(false);
    };

    const downloadXml = (content, filename) => {
        const blob = new Blob([content], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">SEO & Visibility</div>
                    <h1>Sitemap Manager <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <Link to="/admin/dashboard" className="btn btn-ghost" style={{ borderColor: 'var(--border)', color: 'var(--text2)' }}>
                        Dashboard
                    </Link>
                    <button
                        className="btn btn-primary"
                        onClick={handleGenerate}
                        disabled={loading}
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
                        {loading ? 'Generating...' : 'Regenerate Overall'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="stat-card">
                    <div className="s-label">Index Sitemap</div>
                    <div className="s-val text-sm font-mono truncate">sitemap.xml</div>
                </div>
                <div className="stat-card green">
                    <div className="s-label">Last Generated</div>
                    <div className="s-val text-[14px]">{lastGenerated || 'Never'}</div>
                </div>
                <div className="stat-card purple">
                    <div className="s-label">Status</div>
                    <div className="s-val text-[14px]">
                        {status?.status === 'success' ? (
                            <span className="flex items-center gap-1"><Check size={14} /> Healthy</span>
                        ) : (
                            <span className="flex items-center gap-1"><AlertTriangle size={14} /> Missing</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <h2 className="text-lg font-bold text-[#20222D] flex items-center">
                        <FileCode size={20} className="mr-2 text-[#A2CB52]" />
                        XML Sitemap Files
                    </h2>
                    <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider font-bold">Generated from live database content</p>
                </div>

                <div className="divide-y divide-gray-100">
                    {[
                        { id: 'index', name: 'Sitemap Index', file: 'sitemap.xml' },
                        { id: 'main', name: 'Main Pages', file: 'sitemap-main.xml' },
                        { id: 'blog', name: 'Blog Posts', file: 'sitemap-blog.xml' },
                        { id: 'resources', name: 'Resources & Webinars', file: 'sitemap-resources.xml' },
                        { id: 'glossary', name: 'Glossary Terms', file: 'sitemap-glossary.xml' },
                        { id: 'authors', name: 'Author Profiles', file: 'sitemap-authors.xml' }
                    ].map(item => (
                        <div key={item.id} className="p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                                    <Globe size={20} />
                                </div>
                                <div>
                                    <div className="font-bold text-[#20222D]">{item.name}</div>
                                    <a
                                        href={`/${item.file}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-[#A2CB52] font-mono hover:underline flex items-center gap-1"
                                    >
                                        {item.file} <ExternalLink size={10} />
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    className="p-2 text-gray-400 hover:text-[#A2CB52] hover:bg-[#ECF5DC] rounded-lg transition-all"
                                    onClick={() => status?.[item.id] && downloadXml(status[item.id], item.file)}
                                    title="Download XML"
                                    disabled={!status?.[item.id]}
                                >
                                    <Download size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-8 bg-[#20222D] p-8 rounded-2xl text-white shadow-xl relative overflow-hidden">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h3 className="text-xl font-bold mb-2">Automated Optimization</h3>
                        <p className="text-gray-400 text-sm max-w-lg">Sitemaps are automatically updated when you add new blog posts, glossary terms, or webinars. This ensures Google always has the latest version of your site index.</p>
                    </div>
                    <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl backdrop-blur-md">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="autoUpdate"
                                checked={config.autoUpdate}
                                onChange={(e) => setConfig({ ...config, autoUpdate: e.target.checked })}
                                className="w-4 h-4 rounded border-gray-700 text-[#A2CB52] focus:ring-[#A2CB52] bg-transparent"
                            />
                            <label htmlFor="autoUpdate" className="text-sm font-bold">Enabled</label>
                        </div>
                    </div>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] opacity-10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            </div>
        </div>
    );
};

export default SitemapManager;
